import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";

// To Fetch All User List
export const allUsersList = createAsyncThunk("user/allUsersList", async ({ search, status, verification_status, page, limit, city_id, profession_id, is_migrated, must_reset_password, advancedFilters = {} }, { rejectWithValue }) => {
  try {
    const cityParams = city_id ? (Array.isArray(city_id) ? city_id.join(',') : city_id) : "";
    const professionParams = profession_id ? (Array.isArray(profession_id) ? profession_id.join(',') : profession_id) : "";

    const queryParams = new URLSearchParams({
      search: search || "",
      status: status || "",
      limit: limit || 10,
      page: page || 1,
      city_id: cityParams,
      profession_id: professionParams,
    });

    if (is_migrated !== undefined && is_migrated !== "") queryParams.append("is_migrated", is_migrated);
    if (must_reset_password !== undefined && must_reset_password !== "") queryParams.append("must_reset_password", must_reset_password);
    if (verification_status !== undefined && verification_status !== "") queryParams.append("verification_status", verification_status);

    Object.entries(advancedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          if (value.length > 0) queryParams.append(key, value.join(','));
        } else {
          queryParams.append(key, value);
        }
      }
    });

    const response = await Axios.get(`${process.env.REACT_APP_BASE_URL}/app-users?${queryParams.toString()}`, authHeaders());
    return response.data; //success Case
  } catch (error) {
    return rejectWithValue(error.message); // Error case
  }
});

export const changeUserStarStatus = createAsyncThunk(
  "user/changeUserStarStatus",
  async ({ id, is_star, is_star_user }, { rejectWithValue }) => {
    try {
      const starValue = is_star !== undefined ? is_star : is_star_user;
      const res = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/star-status`,
        { is_star: starValue },
        authHeaders()
      );
      return { id, is_star: starValue, is_star_user: starValue, message: res.data.message };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// Export All Users to Excel
export const exportAllUsers = createAsyncThunk(
  "user/exportAllUsers",
  async ({ search, status, verification_status, city_id, profession_id, is_migrated, must_reset_password, advancedFilters = {} } = {}, { rejectWithValue }) => {
    try {
      const cityParams = city_id ? (Array.isArray(city_id) ? city_id.join(",") : city_id) : "";
      const professionParams = profession_id ? (Array.isArray(profession_id) ? profession_id.join(",") : profession_id) : "";

      const queryParams = new URLSearchParams({
        search: search || "",
        status: status || "",
        city_id: cityParams,
        profession_id: professionParams,
      });

      if (is_migrated !== undefined && is_migrated !== "") queryParams.append("is_migrated", is_migrated);
      if (must_reset_password !== undefined && must_reset_password !== "") queryParams.append("must_reset_password", must_reset_password);
      if (verification_status !== undefined && verification_status !== "") queryParams.append("verification_status", verification_status);

      Object.entries(advancedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            if (value.length > 0) queryParams.append(key, value.join(','));
          } else {
            queryParams.append(key, value);
          }
        }
      });

      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/app-users/export?${queryParams.toString()}`,
        { ...authHeaders(), responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `users_export_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchProfessions = createAsyncThunk(
  "user/fetchProfessions",
  async ({ search, page, limit } = {}, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/professions?search=${search || ""}&page=${page || 1}&limit=${limit || 100}`,
        authHeaders()
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchUserDetail = createAsyncThunk(
  "user/fetchUserDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}`,
        authHeaders()
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message
      );
    }
  }
);

export const updateUserDetails = createAsyncThunk(
  "user/updateUserDetails",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const assignUserPlan = createAsyncThunk(
  "user/assignUserPlan",
  async ({ user_id, plan_id }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/subscriptions/assign`,
        { user_id, plan_id },
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const reverseUserSubscription = createAsyncThunk(
  "user/reverseUserSubscription",
  async ({ subscription_id }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/subscriptions/reverse`,
        { subscription_id },
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);
export const manualVerifyUserIdentity = createAsyncThunk(
  "user/manualVerifyUserIdentity",
  async (user_id, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/user-verifications/manual-verify`,
        { user_id },
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const unverifyUserIdentity = createAsyncThunk(
  "user/unverifyUserIdentity",
  async (user_id, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/user-verifications/unverify`,
        { user_id },
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const addUser = createAsyncThunk(
  "user/addUser",
  async (data, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/app-users`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const removeUserPhoto = createAsyncThunk(
  "user/removeUserPhoto",
  async ({ id, type }, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/photo`,
        {
          ...authHeaders(),
          data: { type },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchUserPosts = createAsyncThunk(
  "user/fetchUserPosts",
  async ({ id, page }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/posts?page=${page}&limit=10`,
        authHeaders()
      );
      return { data: response.data.data, page };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const createUserPost = createAsyncThunk(
  "user/createUserPost",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/posts`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const updateUserPost = createAsyncThunk(
  "user/updateUserPost",
  async ({ id, postId, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/posts/${postId}`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteUserPost = createAsyncThunk(
  "user/deleteUserPost",
  async ({ id, postId }, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/posts/${postId}`,
        authHeaders()
      );
      return { postId, ...response.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const changeUserStatus = createAsyncThunk(
  "user/changeUserStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/status`,
        { status },
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  "user/changeUserPassword",
  async ({ id, password }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/change-password`,
        { password },
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteAppUser = createAsyncThunk(
  "user/deleteAppUser",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}`,
        authHeaders()
      );
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchAppUserStats = createAsyncThunk(
  "user/fetchAppUserStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/app-users/stats?t=${new Date().getTime()}`,
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message
      );
    }
  }
);

export const fetchUserFollowers = createAsyncThunk(
  "user/fetchUserFollowers",
  async ({ id, page, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/followers?page=${page}&limit=${limit}${search ? `&search=${search}` : ""}`,
        authHeaders()
      );
      return { data: response.data.data.data, page, total: response.data.data.pagination.total };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchPostLikes = createAsyncThunk(
  "user/fetchPostLikes",
  async ({ id, page, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/posts/${id}/reactions?page=${page}&limit=${limit}&type=like`,
        authHeaders()
      );
      const interactions = response.data.data || [];
      const users = interactions.map(i => i.user).filter(u => u);
      return { data: users, page, total: response.data.meta.total };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchUserConnections = createAsyncThunk(
  "user/fetchUserConnections",
  async ({ id, page, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/connections?page=${page}&limit=${limit}${search ? `&search=${search}` : ""}`,
        authHeaders()
      );
      return { data: response.data.data.data, page, total: response.data.data.pagination.total };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchUserFollowing = createAsyncThunk(
  "user/fetchUserFollowing",
  async ({ id, page, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/following?page=${page}&limit=${limit}${search ? `&search=${search}` : ""}`,
        authHeaders()
      );
      return { data: response.data.data.data, page, total: response.data.data.pagination.total };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchCompanyFollowers = createAsyncThunk(
  "user/fetchCompanyFollowers",
  async ({ id, page, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/companies/${id}/followers?page=${page}&limit=${limit}${search ? `&search=${search}` : ""}`,
        authHeaders()
      );
      return {
        data: response.data.data.data,
        page,
        total: response.data.data.pagination.total
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const addUserExperience = createAsyncThunk(
  "user/addUserExperience",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/experiences`,
        data,
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const updateUserExperience = createAsyncThunk(
  "user/updateUserExperience",
  async ({ id, experienceId, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/experiences/${experienceId}`,
        data,
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteUserExperience = createAsyncThunk(
  "user/deleteUserExperience",
  async ({ id, experienceId }, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/experiences/${experienceId}`,
        authHeaders()
      );
      return { experienceId, ...response.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchProfileConfig = createAsyncThunk(
  "allUsers/fetchProfileConfig",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/app-users/config`,
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const addUserEducation = createAsyncThunk(
  "user/addUserEducation",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/education`,
        data,
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const updateUserEducation = createAsyncThunk(
  "user/updateUserEducation",
  async ({ id, educationId, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/education/${educationId}`,
        data,
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteUserEducation = createAsyncThunk(
  "user/deleteUserEducation",
  async ({ id, educationId }, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/education/${educationId}`,
        authHeaders()
      );
      return { educationId, ...response.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const addUserProduct = createAsyncThunk(
  "user/addUserProduct",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/products`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const updateUserProduct = createAsyncThunk(
  "user/updateUserProduct",
  async ({ id, productId, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/products/${productId}`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteUserProduct = createAsyncThunk(
  "allUsers/deleteUserProduct",
  async ({ id, productId }, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/products/${productId}`,
        authHeaders()
      );
      return { productId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addUserAward = createAsyncThunk(
  "allUsers/addUserAward",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/awards`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserAward = createAsyncThunk(
  "allUsers/updateUserAward",
  async ({ id, awardId, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/awards/${awardId}`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteUserAward = createAsyncThunk(
  "allUsers/deleteUserAward",
  async ({ id, awardId }, { rejectWithValue }) => {
    try {
      await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/awards/${awardId}`,
        authHeaders()
      );
      return { awardId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addUserCertification = createAsyncThunk(
  "allUsers/addUserCertification",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/certifications`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserCertification = createAsyncThunk(
  "allUsers/updateUserCertification",
  async ({ id, certificationId, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/certifications/${certificationId}`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteUserCertification = createAsyncThunk(
  "allUsers/deleteUserCertification",
  async ({ id, certificationId }, { rejectWithValue }) => {
    try {
      await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/certifications/${certificationId}`,
        authHeaders()
      );
      return { certificationId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addUserTestimonial = createAsyncThunk(
  "allUsers/addUserTestimonial",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/testimonials`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserTestimonial = createAsyncThunk(
  "allUsers/updateUserTestimonial",
  async ({ id, testimonialId, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/testimonials/${testimonialId}`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteUserTestimonial = createAsyncThunk(
  "allUsers/deleteUserTestimonial",
  async ({ id, testimonialId }, { rejectWithValue }) => {
    try {
      await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/testimonials/${testimonialId}`,
        authHeaders()
      );
      return { testimonialId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addUserCompany = createAsyncThunk(
  "allUsers/addUserCompany",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}/app-users/${id}/companies`,
        data,
        {
          ...authHeaders(),
          headers: {
            ...authHeaders().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const fetchUserMarketplaceListings = createAsyncThunk(
  "allUsers/fetchUserMarketplaceListings",
  async ({ id, page, limit }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/marketplace/user-listing?seller_id=${id}&page=${page || 1}&limit=${limit || 10}`,
        authHeaders()
      );
      return {
        data: response.data.data.data,
        page,
        total: response.data.data.pagination.total
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchUserMarketplaceRequirements = createAsyncThunk(
  "allUsers/fetchUserMarketplaceRequirements",
  async ({ id, page, limit }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/marketplace/requirements?user_id=${id}&page=${page || 1}&limit=${limit || 10}`,
        authHeaders()
      );
      return {
        data: response.data.data,
        page,
        total: response.data.meta.total
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchUserSubscriptions = createAsyncThunk(
  "allUsers/fetchUserSubscriptions",
  async ({ id, page, limit }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/subscriptions/history?user_id=${id}&page=${page || 1}&limit=${limit || 10}`,
        authHeaders()
      );
      return {
        data: response.data.data,
        page,
        total: response.data.meta?.total || 0
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch User Verifications
export const fetchUserVerifications = createAsyncThunk(
  "allUsers/fetchUserVerifications",
  async ({ id, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`${process.env.REACT_APP_BASE_URL}/user-verifications?user_id=${id}&page=${page}&limit=${limit}`, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addUserWorkRegion = createAsyncThunk(
  "allUsers/addUserWorkRegion",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`${process.env.REACT_APP_BASE_URL}/app-users/${id}/work-regions`, data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserWorkRegion = createAsyncThunk(
  "allUsers/updateUserWorkRegion",
  async ({ id, regionId, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`${process.env.REACT_APP_BASE_URL}/app-users/${id}/work-regions/${regionId}`, data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteUserWorkRegion = createAsyncThunk(
  "allUsers/deleteUserWorkRegion",
  async ({ id, regionId }, { rejectWithValue }) => {
    try {
      await Axios.delete(`${process.env.REACT_APP_BASE_URL}/app-users/${id}/work-regions/${regionId}`, authHeaders());
      return { regionId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addUserContactPurpose = createAsyncThunk(
  "allUsers/addUserContactPurpose",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`${process.env.REACT_APP_BASE_URL}/app-users/${id}/contact-purposes`, data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserContactPurpose = createAsyncThunk(
  "allUsers/updateUserContactPurpose",
  async ({ id, purposeId, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`${process.env.REACT_APP_BASE_URL}/app-users/${id}/contact-purposes/${purposeId}`, data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteUserContactPurpose = createAsyncThunk(
  "allUsers/deleteUserContactPurpose",
  async ({ id, purposeId }, { rejectWithValue }) => {
    try {
      await Axios.delete(`${process.env.REACT_APP_BASE_URL}/app-users/${id}/contact-purposes/${purposeId}`, authHeaders());
      return { purposeId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const allUsersSlice = createSlice({
  name: "allUsers",
  initialState: {
    error: null,
    isLoading: false,
    banksList: [],
    branchList: [],
    state: [],
    allUsers: [],
    requested: [],
    changeUserStatus: {},
    addUser: {},
    userDetail: null,
    updateUserLoading: false,
    removePhotoLoading: false,
    userPosts: [],
    postsLoading: false,
    createPostLoading: false,
    updatePostLoading: false,
    deletePostLoading: false,
    marketplaceListings: [],
    marketplaceListingsTotal: 0,
    marketplaceListingsLoading: false,
    marketplaceRequirements: [],
    marketplaceRequirementsLoading: false,
    marketplaceRequirementsTotal: 0,
    userSubscriptions: [],
    userSubscriptionsTotal: 0,
    userSubscriptionsLoading: false,
    userVerifications: [],
    userVerificationsTotal: 0,
    userVerificationsLoading: false,
    experienceLoading: false,
    appUserStats: null,
    appUserStatsLoading: false,
    profileConfig: null,
    profileConfigLoading: false,
    educationLoading: false,
    productLoading: false,
    awardLoading: false,
    certificationLoading: false,
    testimonialLoading: false,
    professions: [],
    professionsLoading: false,
    companyLoading: false,
    networkingUsers: [],
    networkingLoading: false,
    networkingTotal: 0,
    isDetailLoading: false,
    changePasswordLoading: false,
    deleteUserLoading: false,
  },
  reducers: {
    patchUserPhoto: (state, action) => {
      const { type, photoData } = action.payload;
      if (state.userDetail && state.userDetail.profile) {
        state.userDetail.profile[type] = photoData;
      }
    },
    resetUserDetail: (state) => {
      state.userDetail = null;
      state.userPosts = [];
      state.postsLoading = false;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    // request to get all bank list
    builder
      .addCase(allUsersList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(allUsersList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allUsers = action.payload.data;
      })
      .addCase(allUsersList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserDetail.pending, (state) => {
        state.isLoading = true;
        // Don't clear state on pending to avoid flickering during refreshes
      })
      .addCase(fetchUserDetail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userDetail = action.payload.data;
      })
      .addCase(fetchUserDetail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(changeUserStarStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(changeUserStarStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const { is_star } = action.payload;
        if (state.userDetail) {
          state.userDetail.is_star = is_star;
          state.userDetail.is_star_user = is_star;
        }
      })
      .addCase(changeUserStarStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserDetails.pending, (state) => {
        state.updateUserLoading = true;
      })
      .addCase(updateUserDetails.fulfilled, (state, action) => {
        state.updateUserLoading = false;
        const updatedUser = action.payload?.data;
        if (updatedUser) {
          // 1. Optimistically update the user detail state with new data
          if (state.userDetail && state.userDetail.id === updatedUser.id) {
            state.userDetail = {
              ...state.userDetail,
              ...updatedUser,
              profile: {
                ...state.userDetail.profile,
                ...(updatedUser.profile || {})
              }
            };
          }
          // 2. Optimistically update the user inside the list data array if present
          if (state.allUsers?.data) {
            const index = state.allUsers.data.findIndex(u => u.id === updatedUser.id);
            if (index !== -1) {
              const currentItem = state.allUsers.data[index];
              state.allUsers.data[index] = {
                ...currentItem,
                first_name: updatedUser.first_name || currentItem.first_name,
                last_name: updatedUser.last_name !== undefined ? updatedUser.last_name : currentItem.last_name,
                email: updatedUser.email !== undefined ? updatedUser.email : currentItem.email,
                mobile_number: updatedUser.mobile_number || currentItem.mobile_number,
                status: updatedUser.status || currentItem.status,
                city: updatedUser.profile?.city ? { id: updatedUser.profile.city.id, name: updatedUser.profile.city.name } : currentItem.city,
                profession: updatedUser.profile?.profession ? { id: updatedUser.profile.profession.id, name: updatedUser.profile.profession.name } : currentItem.profession,
                profile: {
                  ...currentItem.profile,
                  profile_photo: updatedUser.profile?.profile_photo ?? currentItem.profile?.profile_photo
                }
              };
            }
          }
        }
      })
      .addCase(updateUserDetails.rejected, (state, action) => {
        state.updateUserLoading = false;
        state.error = action.payload;
      })
      .addCase(addUser.pending, (state) => {
        state.updateUserLoading = true;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.updateUserLoading = false;
        const newUser = action.payload.data;
        if (newUser && state.allUsers?.data) {
          state.allUsers.data = [newUser, ...state.allUsers.data];
          if (state.allUsers.pagination) {
            state.allUsers.pagination.total += 1;
          }
          if (state.appUserStats) {
            state.appUserStats.total += 1;
            state.appUserStats.active += 1;
            state.appUserStats.not_migrated += 1;
          }
        }

      })
      .addCase(addUser.rejected, (state, action) => {
        state.updateUserLoading = false;
        state.error = action.payload;
      })
      .addCase(removeUserPhoto.pending, (state) => {
        state.removePhotoLoading = true;
      })
      .addCase(removeUserPhoto.fulfilled, (state) => {
        state.removePhotoLoading = false;
      })
      .addCase(removeUserPhoto.rejected, (state, action) => {
        state.removePhotoLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserPosts.pending, (state, action) => {
        state.postsLoading = true;
        if (action.meta.arg.page === 1) {
          state.userPosts = [];
        }
      })
      .addCase(fetchUserPosts.fulfilled, (state, action) => {
        const { data, page } = action.payload;
        state.postsLoading = false;

        if (page === 1) {
          state.userPosts = data;
        } else {
          state.userPosts = [...state.userPosts, ...data];
        }
      })
      .addCase(fetchUserPosts.rejected, (state, action) => {
        state.postsLoading = false;
        state.error = action.payload;
      })
      .addCase(createUserPost.pending, (state) => {
        state.createPostLoading = true;
      })
      .addCase(createUserPost.fulfilled, (state, action) => {
        state.createPostLoading = false;
        if (state.userDetail) {
          state.userDetail.posts_count = parseInt(state.userDetail.posts_count || 0) + 1;
        }
        state.userPosts = [action.payload.data, ...state.userPosts];
      })
      .addCase(createUserPost.rejected, (state, action) => {
        state.createPostLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserPost.pending, (state) => {
        state.updatePostLoading = true;
      })
      .addCase(updateUserPost.fulfilled, (state, action) => {
        state.updatePostLoading = false;
        const index = state.userPosts.findIndex(post => post.id === action.payload.data.id);
        if (index !== -1) {
          state.userPosts[index] = action.payload.data;
        }
      })
      .addCase(updateUserPost.rejected, (state, action) => {
        state.updatePostLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteUserPost.pending, (state) => {
        state.deletePostLoading = true;
      })
      .addCase(deleteUserPost.fulfilled, (state, action) => {
        state.deletePostLoading = false;
        if (state.userDetail && state.userDetail.posts_count > 0) {
          state.userDetail.posts_count -= 1;
        }
        state.userPosts = state.userPosts.filter(post => post.id !== action.payload.postId);
      })
      .addCase(deleteUserPost.rejected, (state, action) => {
        state.deletePostLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAppUserStats.pending, (state) => {
        state.appUserStatsLoading = true;
      })
      .addCase(fetchAppUserStats.fulfilled, (state, action) => {
        state.appUserStatsLoading = false;
        state.appUserStats = action.payload.data;
      })
      .addCase(fetchAppUserStats.rejected, (state, action) => {
        state.appUserStatsLoading = false;
        state.error = action.payload;
      })
      .addCase(changeUserStatus.fulfilled, (state, action) => {
        const { id, status: newStatus } = action.meta.arg;

        // 1. Update the user status in the list if present
        if (state.allUsers?.data) {
          const userIndex = state.allUsers.data.findIndex(u => u.id === id);
          if (userIndex !== -1) {
            const oldStatus = state.allUsers.data[userIndex].status;
            state.allUsers.data[userIndex].status = newStatus;

            // 2. Optimistically update counts in appUserStats
            if (state.appUserStats && oldStatus !== newStatus) {
              // Decrement old
              if (state.appUserStats[oldStatus] !== undefined && state.appUserStats[oldStatus] > 0) {
                state.appUserStats[oldStatus] -= 1;
              }
              // Increment new
              if (state.appUserStats[newStatus] !== undefined) {
                state.appUserStats[newStatus] += 1;
              }
            }
          }
        }

        // 3. Update the userDetail if the ID matches
        if (state.userDetail && state.userDetail.id === id) {
          state.userDetail.status = newStatus;
        }
      })
      .addCase(fetchUserSubscriptions.pending, (state, action) => {
        if (!action.meta.arg.page || action.meta.arg.page === 1) {
          state.userSubscriptionsLoading = true;
        }
      })
      .addCase(fetchUserSubscriptions.fulfilled, (state, action) => {
        state.userSubscriptionsLoading = false;
        state.userSubscriptions = action.payload.data;
        state.userSubscriptionsTotal = action.payload.total;
      })
      .addCase(fetchUserSubscriptions.rejected, (state, action) => {
        state.userSubscriptionsLoading = false;
        state.error = action.payload?.message || "Failed to fetch user subscriptions";
      })
      
      // Fetch User Verifications
      .addCase(fetchUserVerifications.pending, (state) => {
        state.userVerificationsLoading = true;
      })
      .addCase(fetchUserVerifications.fulfilled, (state, action) => {
        state.userVerificationsLoading = false;
        state.userVerifications = action.payload?.data?.data || [];
        state.userVerificationsTotal = action.payload?.data?.meta?.total || 0;
      })
      .addCase(fetchUserVerifications.rejected, (state, action) => {
        state.userVerificationsLoading = false;
        state.error = action.payload;
      })
      .addCase(addUserExperience.pending, (state) => {
        state.experienceLoading = true;
      })
      .addCase(addUserExperience.fulfilled, (state, action) => {
        state.experienceLoading = false;
        if (state.userDetail) {
          state.userDetail.experiences = [action.payload.data, ...(state.userDetail.experiences || [])];
        }
      })
      .addCase(addUserExperience.rejected, (state, action) => {
        state.experienceLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserExperience.pending, (state) => {
        state.experienceLoading = true;
      })
      .addCase(updateUserExperience.fulfilled, (state, action) => {
        state.experienceLoading = false;
        if (state.userDetail && state.userDetail.experiences) {
          const index = state.userDetail.experiences.findIndex(exp => exp.id === action.payload.data.id);
          if (index !== -1) {
            state.userDetail.experiences[index] = action.payload.data;
          }
        }
      })
      .addCase(updateUserExperience.rejected, (state, action) => {
        state.experienceLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteUserExperience.pending, (state) => {
        state.experienceLoading = true;
      })
      .addCase(deleteUserExperience.fulfilled, (state, action) => {
        state.experienceLoading = false;
        if (state.userDetail && state.userDetail.experiences) {
          state.userDetail.experiences = state.userDetail.experiences.filter(exp => exp.id !== action.payload.experienceId);
        }
      })
      .addCase(deleteUserExperience.rejected, (state, action) => {
        state.experienceLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchProfileConfig.pending, (state) => {
        state.profileConfigLoading = true;
      })
      .addCase(fetchProfileConfig.fulfilled, (state, action) => {
        state.profileConfigLoading = false;
        state.profileConfig = action.payload.data;
      })
      .addCase(fetchProfileConfig.rejected, (state, action) => {
        state.profileConfigLoading = false;
        state.error = action.payload;
      })

      .addCase(addUserEducation.pending, (state) => {
        state.educationLoading = true;
      })
      .addCase(addUserEducation.fulfilled, (state, action) => {
        state.educationLoading = false;
        state.userDetail = {
          ...state.userDetail,
          education: [action.payload.data, ...(state.userDetail?.education || [])],
        };
      })
      .addCase(addUserEducation.rejected, (state, action) => {
        state.educationLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserEducation.pending, (state) => {
        state.educationLoading = true;
      })
      .addCase(updateUserEducation.fulfilled, (state, action) => {
        state.educationLoading = false;
        state.userDetail = {
          ...state.userDetail,
          education: (state.userDetail?.education || []).map((edu) =>
            edu.id === action.payload.data.id ? action.payload.data : edu
          ),
        };
      })
      .addCase(updateUserEducation.rejected, (state, action) => {
        state.educationLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteUserEducation.pending, (state) => {
        state.educationLoading = true;
      })
      .addCase(deleteUserEducation.fulfilled, (state, action) => {
        state.educationLoading = false;
        state.userDetail = {
          ...state.userDetail,
          education: (state.userDetail?.education || []).filter(
            (edu) => edu.id !== action.payload.educationId
          ),
        };
      })
      .addCase(deleteUserEducation.rejected, (state, action) => {
        state.educationLoading = false;
        state.error = action.payload;
      })
      .addCase(addUserProduct.pending, (state) => {
        state.productLoading = true;
      })
      .addCase(addUserProduct.fulfilled, (state, action) => {
        state.productLoading = false;
        state.userDetail = {
          ...state.userDetail,
          products: [action.payload.data, ...(state.userDetail?.products || [])],
          products_count: parseInt(state.userDetail?.products_count || 0) + 1,
        };
      })
      .addCase(addUserProduct.rejected, (state, action) => {
        state.productLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserProduct.pending, (state) => {
        state.productLoading = true;
      })
      .addCase(updateUserProduct.fulfilled, (state, action) => {
        state.productLoading = false;
        state.userDetail = {
          ...state.userDetail,
          products: (state.userDetail?.products || []).map((prod) =>
            prod.id === action.payload.data.id ? action.payload.data : prod
          ),
        };
      })
      .addCase(updateUserProduct.rejected, (state, action) => {
        state.productLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteUserProduct.pending, (state) => {
        state.productLoading = true;
      })
      .addCase(deleteUserProduct.fulfilled, (state, action) => {
        state.productLoading = false;
        state.userDetail = {
          ...state.userDetail,
          products: (state.userDetail?.products || []).filter(
            (prod) => prod.id !== action.payload.productId
          ),
          products_count: Math.max(0, parseInt(state.userDetail?.products_count || 0) - 1),
        };
      })
      .addCase(deleteUserProduct.rejected, (state, action) => {
        state.productLoading = false;
        state.error = action.payload;
      })
      .addCase(addUserAward.pending, (state) => {
        state.awardLoading = true;
      })
      .addCase(addUserAward.fulfilled, (state, action) => {
        state.awardLoading = false;
        state.userDetail = {
          ...state.userDetail,
          awards: [action.payload.data, ...(state.userDetail?.awards || [])],
        };
      })
      .addCase(addUserAward.rejected, (state, action) => {
        state.awardLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserAward.pending, (state) => {
        state.awardLoading = true;
      })
      .addCase(updateUserAward.fulfilled, (state, action) => {
        state.awardLoading = false;
        state.userDetail = {
          ...state.userDetail,
          awards: (state.userDetail?.awards || []).map((award) =>
            award.id === action.payload.data.id ? action.payload.data : award
          ),
        };
      })
      .addCase(updateUserAward.rejected, (state, action) => {
        state.awardLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteUserAward.pending, (state) => {
        state.awardLoading = true;
      })
      .addCase(deleteUserAward.fulfilled, (state, action) => {
        state.awardLoading = false;
        state.userDetail = {
          ...state.userDetail,
          awards: (state.userDetail?.awards || []).filter(
            (award) => award.id !== action.payload.awardId
          ),
        };
      })
      .addCase(deleteUserAward.rejected, (state, action) => {
        state.awardLoading = false;
        state.error = action.payload;
      })
      .addCase(addUserCertification.pending, (state) => {
        state.certificationLoading = true;
      })
      .addCase(addUserCertification.fulfilled, (state, action) => {
        state.certificationLoading = false;
        state.userDetail = {
          ...state.userDetail,
          certifications: [action.payload.data, ...(state.userDetail?.certifications || [])],
        };
      })
      .addCase(addUserCertification.rejected, (state, action) => {
        state.certificationLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserCertification.pending, (state) => {
        state.certificationLoading = true;
      })
      .addCase(updateUserCertification.fulfilled, (state, action) => {
        state.certificationLoading = false;
        state.userDetail = {
          ...state.userDetail,
          certifications: (state.userDetail?.certifications || []).map((cert) =>
            cert.id === action.payload.data.id ? action.payload.data : cert
          ),
        };
      })
      .addCase(updateUserCertification.rejected, (state, action) => {
        state.certificationLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteUserCertification.pending, (state) => {
        state.certificationLoading = true;
      })
      .addCase(deleteUserCertification.fulfilled, (state, action) => {
        state.certificationLoading = false;
        state.userDetail = {
          ...state.userDetail,
          certifications: (state.userDetail?.certifications || []).filter(
            (cert) => cert.id !== action.payload.certificationId
          ),
        };
      })
      .addCase(deleteUserCertification.rejected, (state, action) => {
        state.certificationLoading = false;
        state.error = action.payload;
      })
      .addCase(addUserTestimonial.pending, (state) => {
        state.testimonialLoading = true;
      })
      .addCase(addUserTestimonial.fulfilled, (state, action) => {
        state.testimonialLoading = false;
        state.userDetail = {
          ...state.userDetail,
          testimonials: [action.payload.data, ...(state.userDetail?.testimonials || [])],
        };
      })
      .addCase(addUserTestimonial.rejected, (state, action) => {
        state.testimonialLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserTestimonial.pending, (state) => {
        state.testimonialLoading = true;
      })
      .addCase(updateUserTestimonial.fulfilled, (state, action) => {
        state.testimonialLoading = false;
        state.userDetail = {
          ...state.userDetail,
          testimonials: (state.userDetail?.testimonials || []).map((test) =>
            test.id === action.payload.data.id ? action.payload.data : test
          ),
        };
      })
      .addCase(updateUserTestimonial.rejected, (state, action) => {
        state.testimonialLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteUserTestimonial.pending, (state) => {
        state.testimonialLoading = true;
      })
      .addCase(deleteUserTestimonial.fulfilled, (state, action) => {
        state.testimonialLoading = false;
        state.userDetail = {
          ...state.userDetail,
          testimonials: (state.userDetail?.testimonials || []).filter(
            (test) => test.id !== action.payload.testimonialId
          ),
        };
      })
      .addCase(deleteUserTestimonial.rejected, (state, action) => {
        state.testimonialLoading = false;
        state.error = action.payload;
      })
      .addCase(addUserCompany.pending, (state) => {
        state.companyLoading = true;
      })
      .addCase(addUserCompany.fulfilled, (state, action) => {
        state.companyLoading = false;
        if (state.userDetail) {
          if (!state.userDetail.companies) {
            state.userDetail.companies = [];
          }
          state.userDetail.companies.unshift(action.payload.data);
        }
      })
      .addCase(addUserCompany.rejected, (state, action) => {
        state.companyLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserFollowers.pending, (state, action) => {
        state.networkingLoading = true;
        if (action.meta.arg.page === 1) state.networkingUsers = [];
      })
      .addCase(fetchUserFollowers.fulfilled, (state, action) => {
        state.networkingLoading = false;
        const { data, page, total } = action.payload;
        state.networkingTotal = total;
        if (page === 1) {
          state.networkingUsers = data;
        } else {
          state.networkingUsers = [...state.networkingUsers, ...data];
        }
      })
      .addCase(fetchUserFollowers.rejected, (state, action) => {
        state.networkingLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPostLikes.pending, (state, action) => {
        state.networkingLoading = true;
        if (action.meta.arg.page === 1) state.networkingUsers = [];
      })
      .addCase(fetchPostLikes.fulfilled, (state, action) => {
        state.networkingLoading = false;
        const { data, page, total } = action.payload;
        state.networkingTotal = total;
        if (page === 1) {
          state.networkingUsers = data;
        } else {
          state.networkingUsers = [...state.networkingUsers, ...data];
        }
      })
      .addCase(fetchPostLikes.rejected, (state, action) => {
        state.networkingLoading = false;
        state.error = action.payload;

      })
      .addCase(fetchUserConnections.pending, (state, action) => {
        state.networkingLoading = true;
        if (action.meta.arg.page === 1) state.networkingUsers = [];
      })
      .addCase(fetchUserConnections.fulfilled, (state, action) => {
        state.networkingLoading = false;
        const { data, page, total } = action.payload;
        state.networkingTotal = total;
        if (page === 1) {
          state.networkingUsers = data;
        } else {
          state.networkingUsers = [...state.networkingUsers, ...data];
        }
      })
      .addCase(fetchUserConnections.rejected, (state, action) => {
        state.networkingLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserFollowing.pending, (state, action) => {
        state.networkingLoading = true;
        if (action.meta.arg.page === 1) state.networkingUsers = [];
      })
      .addCase(fetchUserFollowing.fulfilled, (state, action) => {
        state.networkingLoading = false;
        const { data, page, total } = action.payload;
        state.networkingTotal = total;
        if (page === 1) {
          state.networkingUsers = data;
        } else {
          state.networkingUsers = [...state.networkingUsers, ...data];
        }
      })
      .addCase(fetchUserFollowing.rejected, (state, action) => {
        state.networkingLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCompanyFollowers.pending, (state, action) => {
        state.networkingLoading = true;
        if (action.meta.arg.page === 1) state.networkingUsers = [];
      })
      .addCase(fetchCompanyFollowers.fulfilled, (state, action) => {
        state.networkingLoading = false;
        const { data, page, total } = action.payload;
        state.networkingTotal = total;
        if (page === 1) {
          state.networkingUsers = data;
        } else {
          state.networkingUsers = [...state.networkingUsers, ...data];
        }
      })
      .addCase(fetchCompanyFollowers.rejected, (state, action) => {
        state.networkingLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserMarketplaceListings.pending, (state, action) => {
        state.marketplaceListingsLoading = true;
        if (action.meta.arg.page === 1) state.marketplaceListings = [];
      })
      .addCase(fetchUserMarketplaceListings.fulfilled, (state, action) => {
        state.marketplaceListingsLoading = false;
        const { data, page, total } = action.payload;
        state.marketplaceListingsTotal = total;
        if (page === 1) {
          state.marketplaceListings = data;
        } else {
          state.marketplaceListings = [...state.marketplaceListings, ...data];
        }
      })
      .addCase(fetchUserMarketplaceListings.rejected, (state, action) => {
        state.marketplaceListingsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserMarketplaceRequirements.pending, (state, action) => {
        state.marketplaceRequirementsLoading = true;
        if (action.meta.arg.page === 1) state.marketplaceRequirements = [];
      })
      .addCase(fetchUserMarketplaceRequirements.fulfilled, (state, action) => {
        state.marketplaceRequirementsLoading = false;
        const { data, page, total } = action.payload;
        state.marketplaceRequirementsTotal = total;
        if (page === 1) {
          state.marketplaceRequirements = data;
        } else {
          state.marketplaceRequirements = [...state.marketplaceRequirements, ...data];
        }
      })
      .addCase(fetchUserMarketplaceRequirements.rejected, (state, action) => {
        state.marketplaceRequirementsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchProfessions.pending, (state) => {
        state.professionsLoading = true;
      })
      .addCase(fetchProfessions.fulfilled, (state, action) => {
        state.professionsLoading = false;
        state.professions = action.payload.data;
      })
      .addCase(fetchProfessions.rejected, (state, action) => {
        state.professionsLoading = false;
      })
      .addCase(changeUserPassword.pending, (state) => {
        state.changePasswordLoading = true;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.changePasswordLoading = false;
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.changePasswordLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteAppUser.pending, (state) => {
        state.deleteUserLoading = true;
      })
      .addCase(deleteAppUser.fulfilled, (state, action) => {
        state.deleteUserLoading = false;
        const deletedId = action.meta.arg; // The ID passed to the thunk

        if (state.allUsers?.data) {
          // Find the user to get their status before removing
          const userToRemove = state.allUsers.data.find(u => u.id === deletedId);
          const oldStatus = userToRemove?.status;

          // Remove from list
          state.allUsers.data = state.allUsers.data.filter(u => u.id !== deletedId);

          // Update total count in pagination
          if (state.allUsers.pagination) {
            state.allUsers.pagination.total = Math.max(0, state.allUsers.pagination.total - 1);
          }

          // Update stats
          if (state.appUserStats) {
            state.appUserStats.total = Math.max(0, (state.appUserStats.total || 0) - 1);
            if (oldStatus && state.appUserStats[oldStatus] !== undefined) {
              state.appUserStats[oldStatus] = Math.max(0, state.appUserStats[oldStatus] - 1);
            }
          }
        }
      })
      .addCase(deleteAppUser.rejected, (state, action) => {
        state.deleteUserLoading = false;
        state.error = action.payload;
      })
      .addCase(addUserWorkRegion.fulfilled, (state, action) => {
        if (state.userDetail) {
          state.userDetail.work_regions = [...(state.userDetail.work_regions || []), action.payload.data].sort((a, b) => a.sequence - b.sequence);
        }
      })
      .addCase(updateUserWorkRegion.fulfilled, (state, action) => {
        if (state.userDetail) {
          state.userDetail.work_regions = (state.userDetail.work_regions || []).map(r => r.id === action.payload.data.id ? action.payload.data : r).sort((a, b) => a.sequence - b.sequence);
        }
      })
      .addCase(deleteUserWorkRegion.fulfilled, (state, action) => {
        if (state.userDetail) {
          state.userDetail.work_regions = (state.userDetail.work_regions || []).filter(r => r.id !== action.payload.regionId);
        }
      })
      .addCase(addUserContactPurpose.fulfilled, (state, action) => {
        if (state.userDetail) {
          state.userDetail.contact_purposes = [...(state.userDetail.contact_purposes || []), action.payload.data].sort((a, b) => a.sequence - b.sequence);
        }
      })
      .addCase(updateUserContactPurpose.fulfilled, (state, action) => {
        if (state.userDetail) {
          state.userDetail.contact_purposes = (state.userDetail.contact_purposes || []).map(p => p.id === action.payload.data.id ? action.payload.data : p).sort((a, b) => a.sequence - b.sequence);
        }
      })
      .addCase(deleteUserContactPurpose.fulfilled, (state, action) => {
        if (state.userDetail) {
          state.userDetail.contact_purposes = (state.userDetail.contact_purposes || []).filter(p => p.id !== action.payload.purposeId);
        }
      })
      .addCase(manualVerifyUserIdentity.fulfilled, (state) => {
        if (state.userDetail) {
          state.userDetail.identity_verified = true;
          state.userDetail.identity_verified_by_type = "admin";
          state.userDetail.is_mobile_verified = true;
          state.userDetail.is_email_verified = true;
        }
      })
      .addCase(unverifyUserIdentity.fulfilled, (state) => {
        if (state.userDetail) {
          state.userDetail.identity_verified = false;
          state.userDetail.identity_verified_by_type = null;
        }
      });

  },
});

export const { patchUserPhoto, resetUserDetail } = allUsersSlice.actions;

export default allUsersSlice.reducer;
